import { useState, useEffect, useRef } from "react";
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, writeBatch, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { Bell, Users, CalendarCheck, ImageIcon, User, CheckCheck, X } from "lucide-react";

const TYPE_ICONS = {
  new_member:  { icon: Users,        color:"#22c55e",  bg:"rgba(34,197,94,0.12)"  },
  attendance:  { icon: CalendarCheck, color:"#3b82f6", bg:"rgba(59,130,246,0.12)" },
  poster:      { icon: ImageIcon,     color:"#f59e0b", bg:"rgba(245,158,11,0.12)" },
  profile:     { icon: User,          color:"#a855f7", bg:"rgba(168,85,247,0.12)" },
  general:     { icon: Bell,          color:"#78716c", bg:"rgba(255,255,255,0.06)"},
};

function timeAgo(ts) {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60)   return "just now";
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400)return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

export default function NotificationBell() {
  const { currentUser, userProfile } = useAuth();
  const [notifs,  setNotifs]  = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(true);
  const dropRef = useRef(null);

  const role = userProfile?.role || "member";
  const uid  = currentUser?.uid;

  useEffect(() => {
    if (!uid) return;
    // Listen to notifications for this user OR broadcast ("all")
    const q = query(
      collection(db, "notifications"),
      where("recipients", "array-contains-any", [uid, "all"]),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setNotifs(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
    return unsub;
  }, [uid]);

  // Close on outside click
  useEffect(() => {
    function handle(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const unread = notifs.filter(n => !n.readBy?.includes(uid)).length;

  async function markAllRead() {
    const batch = writeBatch(db);
    notifs.filter(n => !n.readBy?.includes(uid)).forEach(n => {
      const ref = doc(db, "notifications", n.id);
      batch.update(ref, { readBy: [...(n.readBy||[]), uid] });
    });
    await batch.commit();
  }

  async function markOneRead(notif) {
    if (notif.readBy?.includes(uid)) return;
    await updateDoc(doc(db, "notifications", notif.id), {
      readBy: [...(notif.readBy||[]), uid]
    });
  }

  return (
    <div ref={dropRef} style={{ position:"relative" }}>
      {/* Bell Button */}
      <button
        onClick={() => { setOpen(o => !o); if (!open && unread > 0) markAllRead(); }}
        style={{
          position:"relative", width:36, height:36,
          background:"rgba(255,255,255,0.06)",
          border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:10, display:"flex", alignItems:"center",
          justifyContent:"center", cursor:"pointer", transition:"all 0.2s",
        }}>
        <Bell size={17} color={unread > 0 ? "#f59e0b" : "#78716c"}/>
        {unread > 0 && (
          <div style={{
            position:"absolute", top:-4, right:-4,
            background:"#ef4444", color:"#fff",
            fontSize:9, fontWeight:800,
            width:16, height:16, borderRadius:"50%",
            display:"flex", alignItems:"center", justifyContent:"center",
            border:"2px solid #111009",
          }}>
            {unread > 9 ? "9+" : unread}
          </div>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:"absolute", top:44, right:0, width:340,
          background:"#111009", border:"1px solid rgba(255,255,255,0.1)",
          borderRadius:14, boxShadow:"0 20px 60px rgba(0,0,0,0.6)",
          zIndex:999, overflow:"hidden",
          animation:"fadeIn 0.15s ease",
        }}>
          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#fef3e2" }}>
              Notifications {unread > 0 && <span style={{ fontSize:11, background:"rgba(239,68,68,0.15)", color:"#ef4444", border:"1px solid rgba(239,68,68,0.25)", padding:"1px 7px", borderRadius:50, marginLeft:6 }}>{unread} new</span>}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {unread > 0 && (
                <button onClick={markAllRead} style={{ background:"none", border:"none", cursor:"pointer", color:"#78716c", fontSize:11, fontWeight:600, fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
                  <CheckCheck size={13}/> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"#78716c" }}>
                <X size={15}/>
              </button>
            </div>
          </div>

          {/* List */}
          <div style={{ maxHeight:380, overflowY:"auto" }}>
            {loading ? (
              <div style={{ padding:"24px", textAlign:"center", fontSize:13, color:"#52504d" }}>Loading...</div>
            ) : notifs.length === 0 ? (
              <div style={{ padding:"40px 20px", textAlign:"center" }}>
                <Bell size={32} color="#1a1714" style={{ marginBottom:10 }}/>
                <div style={{ fontSize:13, color:"#52504d" }}>No notifications yet</div>
              </div>
            ) : notifs.map(n => {
              const isRead = n.readBy?.includes(uid);
              const ti = TYPE_ICONS[n.type] || TYPE_ICONS.general;
              const Icon = ti.icon;
              return (
                <div
                  key={n.id}
                  onClick={() => markOneRead(n)}
                  style={{
                    display:"flex", gap:12, padding:"12px 16px",
                    borderBottom:"1px solid rgba(255,255,255,0.04)",
                    background: isRead ? "transparent" : "rgba(245,158,11,0.04)",
                    cursor:"pointer", transition:"background 0.15s",
                  }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:ti.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Icon size={16} color={ti.color}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight: isRead ? 500 : 700, color: isRead ? "#a8a29e" : "#fef3e2", marginBottom:3, lineHeight:1.4 }}>
                      {n.title}
                    </div>
                    {n.message && (
                      <div style={{ fontSize:12, color:"#52504d", lineHeight:1.5, marginBottom:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {n.message}
                      </div>
                    )}
                    <div style={{ fontSize:11, color:"#3a3835" }}>{timeAgo(n.createdAt)}</div>
                  </div>
                  {!isRead && (
                    <div style={{ width:7, height:7, borderRadius:"50%", background:"#f59e0b", flexShrink:0, marginTop:5 }}/>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {notifs.length > 0 && (
            <div style={{ padding:"10px 16px", borderTop:"1px solid rgba(255,255,255,0.06)", textAlign:"center" }}>
              <span style={{ fontSize:12, color:"#52504d" }}>{notifs.length} total notifications</span>
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
